import React, { type ReactNode, useContext, useMemo } from 'react';

import { Flex } from '@banhala/ui-kit';
import { ScrollContext } from '../../context/ScrollContext';
import { ScrollToBottomButton, ScrollToTopButton } from 'shared/src/component';
import Animated, {
  useAnimatedProps,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { useGoodsPromotionComponents } from '../../queries/useGoodsPromotionComponents';
import { PromotionFloatingButton } from './components/PromotionFloatingButton';
import {
  SCROLL_ANIMATION_DURATION,
  SCROLL_FLOATING_BUTTON_VISIBLE_VALUE,
} from '../../constant/scrollValue';
import { FloatingButtonsContainer } from './components/FloatingButtonsContainer';
import { isAndroid } from 'shared/src/utils';

const FIXED_OFFSET = 16;
const BUTTON_SIZE = 40;

const ButtonContainer: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Flex
    shadowStep={'medium'}
    width={BUTTON_SIZE}
    height={BUTTON_SIZE}
    borderRadius={BUTTON_SIZE * 0.5}
  >
    {children}
  </Flex>
);

const FloatingButtonContent: React.FC<{
  bottomOffset: number;
  stickyItemPositionY: number;
}> = props => {
  const { scrollY, listRef } = useContext(ScrollContext);
  const { data: promotionComponents } =
    useGoodsPromotionComponents('GOODS_DETAIL');

  const floatingButton = useMemo(() => {
    const buttons = promotionComponents.component?.floating_buttons ?? [];
    if (buttons.length === 0) return null;
    return {
      button: buttons[Math.floor(Math.random() * buttons.length)],
      promotionSno: promotionComponents.component?.promotion_sno,
    };
  }, [
    promotionComponents.component?.floating_buttons,
    promotionComponents.component?.promotion_sno,
  ]);

  const style = useAnimatedStyle(() => {
    if (!scrollY) return {};

    return {
      position: 'absolute',
      bottom: props.bottomOffset + FIXED_OFFSET + 32,
      right: FIXED_OFFSET,
      alignItems: 'center',
      gap: 12,
      opacity: isAndroid
        ? scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE
          ? 1
          : 0
        : withTiming(
            scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE ? 1 : 0,
            {
              duration: SCROLL_ANIMATION_DURATION,
            },
          ),
    };
  });

  const animatedProps = useAnimatedProps(() => ({
    pointerEvents:
      scrollY && scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE
        ? ('auto' as const)
        : ('none' as const),
  }));

  return (
    <Animated.View style={style} animatedProps={animatedProps}>
      <ButtonContainer>
        <ScrollToTopButton
          testID={'탑버튼'}
          onPress={() => {
            listRef?.current?.scrollToOffset({ offset: 0, animated: true });
          }}
        />
      </ButtonContainer>
      <ButtonContainer>
        <ScrollToBottomButton
          testID={'바텀버튼'}
          onPress={() => {
            listRef?.current?.scrollToEnd({ animated: true });
          }}
        />
      </ButtonContainer>
      {floatingButton && (
        <PromotionFloatingButton
          button={floatingButton.button}
          buttonIdx={0}
          promotionSno={floatingButton.promotionSno}
        />
      )}
    </Animated.View>
  );
};

export const FloatingButtons: React.FC<{
  bottomOffset: number;
  stickyItemPositionY: number;
}> = props => {
  const { scrollY, listRef } = useContext(ScrollContext);

  const style = useAnimatedStyle(() => {
    if (!scrollY) return {};

    return {
      position: 'absolute',
      bottom: FIXED_OFFSET,
      gap: 12,
      // opacity: interpolate(scrollY.value, [0, 10], [0, 1]),
      opacity: withTiming(
        scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE ? 1 : 0,
        {
          duration: isAndroid
            ? SCROLL_ANIMATION_DURATION * 0.5
            : SCROLL_ANIMATION_DURATION,
        },
      ),
      // opacity: withTiming(scrollY.value > props.stickyItemPositionY ? 1 : 0, {
      //   duration: SCROLL_ANIMATION_DURATION,
      // }),
    };
  });

  const fallbackAnimatedProps = useAnimatedProps(() => ({
    pointerEvents:
      scrollY && scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE
        ? ('auto' as const)
        : ('none' as const),
  }));

  return (
    <FloatingButtonsContainer>
      <React.Suspense
        fallback={
          <Flex
            position={'absolute'}
            right={BUTTON_SIZE + FIXED_OFFSET}
            bottom={props.bottomOffset}
          >
            <Animated.View style={style} animatedProps={fallbackAnimatedProps}>
              <ButtonContainer>
                <ScrollToTopButton
                  testID={'탑버튼'}
                  onPress={() => {
                    listRef?.current?.scrollToOffset({
                      offset: 0,
                      animated: true,
                    });
                  }}
                />
              </ButtonContainer>
              <ButtonContainer>
                <ScrollToBottomButton
                  testID={'바텀버튼'}
                  onPress={() => {
                    listRef?.current?.scrollToEnd({ animated: true });
                  }}
                />
              </ButtonContainer>
            </Animated.View>
          </Flex>
        }
      >
        <FloatingButtonContent
          bottomOffset={props.bottomOffset}
          stickyItemPositionY={props.stickyItemPositionY}
        />
      </React.Suspense>
    </FloatingButtonsContainer>
  );
};
